import { useEffect, useRef, useState } from 'react'
import './advantages.css'

const ADVANTAGES = [
    'Tokens de cortesía para configurar tu cuenta sin costo',
    'Facturación con búsqueda de productos por grupos y nombre',
    'Edición de compradores en tiempo real mientras facturas',
    'Emite proformas aunque no haya conexión a internet',
    'Revisa tus facturas, proformas y notas de crédito sin conexión',
]

const DISPLAY_COUNT = 3
const PAUSE_MS = 3400
// Each slot = bubble height (88px) + gap (12px) = 100px
const SLOT_H = 88
const SLOT_GAP = 12
const SLOT_TOTAL = SLOT_H + SLOT_GAP
// Timings
const EXIT_MS = 860       // bubble 0 exits
const LIFT_GAP_MS = 380   // delay between bubble 1 lift and bubble 2 lift
const ENTER_DELAY_MS = 360 // wait after bubble 2 settles before inserting new bubble
const ACCENT_COLORS = ['#7df0ff', '#ffb27a', '#d8b4ff', '#82ffc7', '#ff82a9']

type Bubble = {
    id: number
    label: string
    accentIndex: number
    // slot: visual row. 0=top, 1=middle, 2=bottom, -1=above screen, 3=below screen
    slot: number
    visible: boolean
}

const createBubble = (
    label: string,
    accentIndex: number,
    id: number,
    slot: number,
    visible = true
): Bubble => ({ id, label, accentIndex, slot, visible })

const initialBubbles = Array.from({ length: DISPLAY_COUNT }, (_, idx) =>
    createBubble(ADVANTAGES[idx % ADVANTAGES.length], idx, idx, idx)
)

function AdvantagesModule() {
    const [bubbles, setBubbles] = useState<Bubble[]>(initialBubbles)
    const rollingRef = useRef(DISPLAY_COUNT % ADVANTAGES.length)
    const idRef = useRef(DISPLAY_COUNT)
    const timersRef = useRef<Array<ReturnType<typeof setTimeout>>>([])
    const rafRef = useRef(0)

    useEffect(() => {
        if (ADVANTAGES.length <= DISPLAY_COUNT) {
            return undefined
        }

        const after = (ms: number, fn: () => void) => {
            const t = setTimeout(() => {
                timersRef.current = timersRef.current.filter(x => x !== t)
                fn()
            }, ms)
            timersRef.current.push(t)
        }

        const runCycle = () => {
            // Step 1 — slide bubble at slot 0 upward out of view
            setBubbles(prev =>
                prev.map((b): Bubble => (b.slot === 0 ? { ...b, slot: -1, visible: false } : b))
            )

            // Step 2 — after exit, lift bubble 1 (slot 1 → slot 0)
            after(EXIT_MS, () => {
                setBubbles(prev =>
                    prev.map((b): Bubble => (b.slot === 1 ? { ...b, slot: 0 } : b))
                )

                // Step 3 — after bubble 1 settles, lift bubble 2 (slot 2 → slot 1)
                after(LIFT_GAP_MS, () => {
                    setBubbles(prev =>
                        prev.map((b): Bubble => (b.slot === 2 ? { ...b, slot: 1 } : b))
                    )

                    // Step 4 — after bubble 2 settles, insert new bubble from below
                    after(LIFT_GAP_MS + ENTER_DELAY_MS, () => {
                        const nextIdx = rollingRef.current
                        const newId = idRef.current++
                        rollingRef.current = (nextIdx + 1) % ADVANTAGES.length

                        // Add new bubble off-screen below (slot 3, invisible)
                        setBubbles(prev => [
                            ...prev.filter(b => b.slot !== -1),
                            createBubble(ADVANTAGES[nextIdx], nextIdx, newId, 3, false),
                        ])

                        // Two rAFs so the browser paints slot=3 before we trigger the transition
                        cancelAnimationFrame(rafRef.current)
                        rafRef.current = requestAnimationFrame(() => {
                            rafRef.current = requestAnimationFrame(() => {
                                setBubbles(prev =>
                                    prev.map((b): Bubble =>
                                        b.id === newId ? { ...b, slot: 2, visible: true } : b
                                    )
                                )
                                after(PAUSE_MS, runCycle)
                            })
                        })
                    })
                })
            })
        }

        after(PAUSE_MS, runCycle)

        return () => {
            timersRef.current.forEach(clearTimeout)
            cancelAnimationFrame(rafRef.current)
        }
    }, [])

    return (
        <section className="advantages-module" id="ventajas">
            <div className="advantages-left">
                <p className="eyebrow">Por qué elegir</p>
                <h2 className="headline">Ventajas</h2>
                <p className="product-name">Factur 24/7</p>
                <p className="description">
                    Diseñada para equipos que necesitan facturar rápido y sin complicaciones,
                    con herramientas pensadas para el día a día de tu negocio.
                </p>
            </div>
            <div className="advantages-right">
                <div className="timeline">
                    <div className="timeline-line" aria-hidden="true" />
                    <ul className="advantages-list">
                        {bubbles.map(bubble => {
                            const accent = ACCENT_COLORS[bubble.accentIndex % ACCENT_COLORS.length]
                            return (
                                <li
                                    key={bubble.id}
                                    className="advantage-bubble"
                                    style={{
                                        transform: `translateY(${bubble.slot * SLOT_TOTAL}px)`,
                                        opacity: bubble.visible ? 1 : 0,
                                        borderColor: accent,
                                        boxShadow: `0 8px 20px ${accent}33`,
                                    }}
                                >
                                    <span className="bubble-indicator" style={{ background: accent }} />
                                    {bubble.label}
                                </li>
                            )
                        })}
                    </ul>
                </div>
            </div>
        </section>
    )
}

export default AdvantagesModule
